/**
 * presale-builder-v4.js — forked from presale-builder.js (v2/v3 shared script)
 *
 * v4 is single-purchase-option only (no Auto-Replenish vs One-Time choice):
 *   - Removed: state.plan, selectPlan(), plan card selection, comparison panel,
 *     ongoing-subscription price, _subscribe_intent cart property
 *   - Removed: MIN_REFILLS gate — CTA unlocks as soon as a bottle is selected;
 *     refills are optional add-ons, never required
 *   - addToCart() always applies the presale discount + free shipping codes
 *     and checks out as a one-time order (no subscription selling plan)
 */

(function () {
  'use strict';

  // ── State ────────────────────────────────────────────────────────
  var state = {
    bottleVariantId:  null,
    bottlePrice:      0,
    bottleTitle:      '',
    bottleImage:      '',
    bottleSubtext:    '',
    bottleColorClass: '',
    bottleBg:         '',
    refills:          {},   // { variantId: { qty, price, title, tagline, image, subtext } }
  };

  var pdpCache = {}; // handle → fetched HTML

  var DISCOUNT           = 0.85; // 15% off
  var DISCOUNT_PCT       = 'PRESALE15';
  var DISCOUNT_SHIP      = 'PRESALEFREE';
  var SERVINGS_PER_REFILL = 5;

  // ── DOM refs ─────────────────────────────────────────────────────
  var dom = {
    bottleCards:        null,
    refillCards:        null,
    summaryList:        null,
    summaryInline:      null,
    section:            null,
    mobileFooter:        null,
    mobileBottleLabel:   null,
    mobileBottleSwatch:  null,
    mobileRefillsRow:    null,
    mobileRefillsText:   null,
    removeIconSvg:      '',
    caretSvg:           '',
  };

  // ── Init ─────────────────────────────────────────────────────────
  function init() {
    dom.bottleCards    = document.querySelectorAll('.spb-bottle-card');
    dom.refillCards    = document.querySelectorAll('.spb-refill-card');
    dom.summaryList    = document.getElementById('spb-summary-list');
    dom.summaryInline  = document.getElementById('spb-summary-inline');
    dom.section        = document.querySelector('.fb-section');

    dom.mobileFooter       = document.getElementById('fb-mobile-footer');
    dom.mobileBottleLabel  = document.querySelector('[data-fb-mobile-bottle-label]');
    dom.mobileBottleSwatch = document.querySelector('[data-fb-mobile-bottle-swatch]');
    dom.mobileRefillsRow   = document.getElementById('fb-mobile-refills-row');
    dom.mobileRefillsText  = document.querySelector('[data-fb-mobile-refills-text]');

    // Show sticky footer only once the builder is scrolled into view
    if (dom.section && dom.mobileFooter) {
      var footerObserver = new IntersectionObserver(function (entries) {
        dom.mobileFooter.classList.toggle('is-visible', entries[0].isIntersecting);
      }, { threshold: 0, rootMargin: '0px 0px -150px 0px' });
      footerObserver.observe(dom.section);
    }

    var iconTpl  = document.getElementById('spb-icon-remove');
    if (iconTpl)  dom.removeIconSvg = iconTpl.innerHTML.trim();
    var caretTpl = document.getElementById('spb-icon-caret');
    if (caretTpl) dom.caretSvg = caretTpl.innerHTML.trim();

    // Bottle click — v1 only (cards with no pill buttons); v2+ uses Add button handler below
    dom.bottleCards.forEach(function (card) {
      card.addEventListener('click', function (e) {
        if (card.classList.contains('is-sold-out')) return;
        if (e.target.closest('.spb-bottle-pill')) return;
        if (e.target.closest('.spb-bottle-add-btn')) return;
        if (!card.querySelector('.spb-bottle-pill')) selectBottle(card);
      });
    });

    // Refill qty buttons
    dom.refillCards.forEach(function (card) {
      var cardInner = card.querySelector('.card__inner');
      var addBtn    = card.querySelector('[data-spb-add]');
      var minus     = card.querySelector('[data-spb-dec]');
      var plus      = card.querySelector('[data-spb-inc]');

      if (cardInner) {
        cardInner.addEventListener('click', function () {
          if (!card.classList.contains('is-active')) changeRefillQty(card, +1);
        });
      }
      if (addBtn) addBtn.addEventListener('click', function (e) { e.stopPropagation(); changeRefillQty(card, +1); });
      if (minus)  minus.addEventListener('click',  function (e) { e.stopPropagation(); changeRefillQty(card, -1); });
      if (plus)   plus.addEventListener('click',   function (e) { e.stopPropagation(); changeRefillQty(card, +1); });
    });

    // ── Bottle color pill click ───────────────────────────────────
    document.addEventListener('click', function (e) {
      var pill = e.target.closest('.spb-bottle-pill');
      if (!pill) return;
      e.stopPropagation();
      if (pill.disabled || pill.classList.contains('is-sold-out')) return;

      var btlCard = pill.closest('.spb-bottle-card');
      if (!btlCard) return;

      // Update active pill within this card only
      btlCard.querySelectorAll('.spb-bottle-pill').forEach(function (p) {
        p.classList.remove('is-active');
      });
      pill.classList.add('is-active');

      // Swap card image and background
      var img   = btlCard.querySelector('.spb-btl-img');
      var inner = btlCard.querySelector('.spb-btl-inner');
      if (img)   img.src = pill.dataset.imageFull || pill.dataset.image;
      if (inner) inner.style.background = pill.dataset.bg;

      // Promote pill data up to the card so selectBottle reads the right variant
      btlCard.dataset.variantId  = pill.dataset.variantId;
      btlCard.dataset.price      = pill.dataset.price;
      btlCard.dataset.title      = pill.dataset.title;
      btlCard.dataset.image      = pill.dataset.image;
      btlCard.dataset.subtext    = pill.dataset.subtext;
      btlCard.dataset.colorClass = pill.dataset.colorClass;
      btlCard.dataset.bg         = pill.dataset.bg;

      // Color change on an already-confirmed bottle deselects it — user must click Add again
      if (btlCard.classList.contains('is-selected')) {
        btlCard.classList.remove('is-selected');
        state.bottleVariantId  = null;
        state.bottlePrice      = 0;
        state.bottleTitle      = '';
        state.bottleImage      = '';
        state.bottleSubtext    = '';
        state.bottleColorClass = '';
        state.bottleBg         = '';
        var addBtn = btlCard.querySelector('.spb-bottle-add-btn');
        if (addBtn) addBtn.textContent = 'Add';
        render();
      }
    });

    // ── Bottle Add button click ───────────────────────────────────
    document.addEventListener('click', function (e) {
      var addBtn = e.target.closest('.spb-bottle-add-btn');
      if (!addBtn) return;
      e.stopPropagation();
      var btlCard = addBtn.closest('.spb-bottle-card');
      if (!btlCard || btlCard.classList.contains('is-sold-out')) return;
      selectBottle(btlCard);
      dom.bottleCards.forEach(function (c) {
        var btn = c.querySelector('.spb-bottle-add-btn');
        if (btn) btn.textContent = c.classList.contains('is-selected') ? 'Added' : 'Add';
      });
    });

    // ── Flavor pill click ─────────────────────────────────────────
    document.addEventListener('click', function (e) {
      var pill = e.target.closest('.spb-flavor-pill');
      if (!pill) return;
      e.stopPropagation();

      var blendCard = pill.closest('.spb-refill-card');
      if (!blendCard) return;

      // Update active pill within this blend card only
      blendCard.querySelectorAll('.spb-flavor-pill').forEach(function (p) {
        p.classList.remove('is-active');
      });
      pill.classList.add('is-active');

      // Swap card image
      var img = blendCard.querySelector('.spb-blend-img');
      if (img) img.src = pill.dataset.imageFull || pill.dataset.image;

      // Promote pill data up to the blend card
      var newVariantId = pill.dataset.variantId;
      blendCard.dataset.variantId = newVariantId;
      blendCard.dataset.price     = pill.dataset.price;
      blendCard.dataset.handle    = pill.dataset.handle;
      blendCard.dataset.title     = pill.dataset.title;
      blendCard.dataset.image     = pill.dataset.image;
      blendCard.dataset.tagline   = pill.dataset.tagline || '';
      blendCard.dataset.subtext   = pill.dataset.subtext || '';

      // Sync qty display + Add/qty toggle visibility for newly selected flavor
      var currentQty = (state.refills[newVariantId] && state.refills[newVariantId].qty) || 0;
      var qtyEl   = blendCard.querySelector('[data-spb-qty]');
      var addBtn  = blendCard.querySelector('[data-spb-add]');
      var qtyWrap = blendCard.querySelector('[data-spb-qty-wrap]');
      if (qtyEl) qtyEl.textContent = currentQty;
      if (currentQty > 0) {
        blendCard.classList.add('is-active');
        if (addBtn)  addBtn.hidden  = true;
        if (qtyWrap) qtyWrap.hidden = false;
      } else {
        blendCard.classList.remove('is-active');
        if (addBtn)  addBtn.hidden  = false;
        if (qtyWrap) qtyWrap.hidden = true;
      }
    });

    // ── PDP drawer helpers ────────────────────────────────────
    function runInjectedScripts(container) {
      container.querySelectorAll('script').forEach(function (old) {
        var s = document.createElement('script');
        s.textContent = old.textContent;
        old.parentNode.replaceChild(s, old);
      });
    }

    function closePdpDrawer() {
      var drawer   = document.getElementById('fb-pdp-drawer');
      var backdrop = document.getElementById('fb-pdp-backdrop');
      if (drawer)   { drawer.classList.remove('is-open'); drawer.setAttribute('aria-hidden', 'true'); }
      if (backdrop) backdrop.classList.remove('is-open');
      document.body.style.overflow = '';
      var panel = document.querySelector('[data-pdp-drawer-nutrition-panel]');
      if (panel) { panel.classList.remove('is-open'); panel.setAttribute('aria-hidden', 'true'); }
    }

    function injectPdpContent(html) {
      var body = document.getElementById('fb-pdp-body');
      if (!body) return;
      body.innerHTML = html;
      runInjectedScripts(body);
    }

    function openPdpDrawer(handle, title) {
      var drawer  = document.getElementById('fb-pdp-drawer');
      var backdrop = document.getElementById('fb-pdp-backdrop');
      var body    = document.getElementById('fb-pdp-body');
      var titleEl = document.getElementById('fb-pdp-title');
      if (titleEl) titleEl.textContent = title || '';
      if (body) body.innerHTML = '<p style="padding:2rem;text-align:center;color:var(--color-text-secondary);">Loading&hellip;</p>';
      if (drawer)   { drawer.classList.add('is-open'); drawer.removeAttribute('aria-hidden'); }
      if (backdrop) backdrop.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      if (pdpCache[handle]) { injectPdpContent(pdpCache[handle]); return; }
      fetch('/products/' + handle + '?view=pdp-drawer-kapsule')
        .then(function (r) { return r.text(); })
        .then(function (html) { pdpCache[handle] = html; injectPdpContent(html); })
        .catch(function () {
          var body = document.getElementById('fb-pdp-body');
          if (body) body.innerHTML = '<p style="padding:2rem;text-align:center;">Unable to load product info.</p>';
        });
    }

    // Event delegation for CTA + PDP drawer
    document.addEventListener('click', function (e) {
      // PDP drawer — open (info button on refill card)
      var infoBtn = e.target.closest('[data-pdp-info-open]');
      if (infoBtn) {
        e.stopPropagation();
        var card = infoBtn.closest('.spb-refill-card');
        if (card) openPdpDrawer(card.dataset.handle, card.dataset.title);
        return;
      }
      // PDP drawer — close
      if (e.target.closest('#fb-pdp-close') || e.target.id === 'fb-pdp-backdrop') {
        closePdpDrawer();
        return;
      }
      // Nutrition sub-panel — open (button inside fetched PDP content)
      var nutBtn = e.target.closest('[data-pdp-drawer-nutrition-open]');
      if (nutBtn) {
        var panel = document.querySelector('[data-pdp-drawer-nutrition-panel]');
        if (panel) { panel.classList.add('is-open'); panel.removeAttribute('aria-hidden'); }
        return;
      }
      // Nutrition sub-panel — close
      if (e.target.closest('[data-pdp-drawer-nutrition-close]')) {
        var panel = document.querySelector('[data-pdp-drawer-nutrition-panel]');
        if (panel) { panel.classList.remove('is-open'); panel.setAttribute('aria-hidden', 'true'); }
        return;
      }
      // Single CTA button (desktop card + mobile footer both use this)
      var ctaBtn = e.target.closest('[data-fb-cta]');
      if (ctaBtn && !ctaBtn.disabled) {
        addToCart();
        return;
      }
      // "Add refills and save" links (in-card prompt + mobile footer) — smooth scroll to Step 2
      var refillsLink = e.target.closest('[data-scroll-refills]');
      if (refillsLink) {
        e.preventDefault();
        var refillSection = document.getElementById('spb-step-refills');
        if (refillSection) refillSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    });

    // PDP drawer + nutrition sub-panel — close on Escape
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var panel = document.querySelector('[data-pdp-drawer-nutrition-panel].is-open');
      if (panel) {
        panel.classList.remove('is-open');
        panel.setAttribute('aria-hidden', 'true');
        return;
      }
      var drawer = document.getElementById('fb-pdp-drawer');
      if (drawer && drawer.classList.contains('is-open')) closePdpDrawer();
    });

    render();
  }

  // ── Bottle ───────────────────────────────────────────────────────
  function selectBottle(card) {
    dom.bottleCards.forEach(function (c) { c.classList.remove('is-selected'); });
    card.classList.add('is-selected');

    state.bottleVariantId  = parseInt(card.dataset.variantId, 10);
    state.bottlePrice      = parseInt(card.dataset.price, 10);
    state.bottleTitle      = card.dataset.title      || '';
    state.bottleImage      = card.dataset.image      || '';
    state.bottleSubtext    = card.dataset.subtext    || '';
    state.bottleColorClass = card.dataset.colorClass || '';
    state.bottleBg         = card.dataset.bg         || '';

    render();

    var refillSection = document.getElementById('spb-step-refills');
    if (refillSection) refillSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // ── Refills (optional add-ons — never gate the CTA) ───────────────
  function changeRefillQty(card, delta) {
    var variantId = card.dataset.variantId;
    var price     = parseInt(card.dataset.price, 10);
    var qtyEl     = card.querySelector('[data-spb-qty]');
    var addBtn    = card.querySelector('[data-spb-add]');
    var qtySel    = card.querySelector('[data-spb-qty-wrap]');

    if (!state.refills[variantId]) {
      state.refills[variantId] = {
        qty:     0,
        price:   price,
        title:   card.dataset.title   || '',
        image:   card.dataset.image   || '',
        tagline: card.dataset.tagline || '',
        subtext: card.dataset.subtext || '',
      };
    }

    var newQty = Math.max(0, state.refills[variantId].qty + delta);
    state.refills[variantId].qty = newQty;
    if (qtyEl) qtyEl.textContent = newQty;

    if (newQty > 0) {
      card.classList.add('is-active');
      if (addBtn) addBtn.hidden = true;
      if (qtySel) qtySel.hidden = false;
    } else {
      card.classList.remove('is-active');
      if (addBtn) addBtn.hidden = false;
      if (qtySel) qtySel.hidden = true;
      delete state.refills[variantId];
    }

    render();
  }

  function removeItem(type, variantId) {
    if (type === 'bottle') {
      state.bottleVariantId  = null;
      state.bottlePrice      = 0;
      state.bottleTitle      = '';
      state.bottleImage      = '';
      state.bottleSubtext    = '';
      state.bottleColorClass = '';
      state.bottleBg         = '';
      dom.bottleCards.forEach(function (c) {
        c.classList.remove('is-selected');
        var btn = c.querySelector('.spb-bottle-add-btn');
        if (btn) btn.textContent = 'Add';
      });
    } else if (type === 'refill') {
      var card = document.querySelector('.spb-refill-card[data-variant-id="' + variantId + '"]');
      if (card) {
        card.classList.remove('is-active');
        var qtyEl  = card.querySelector('[data-spb-qty]');
        var addBtn = card.querySelector('[data-spb-add]');
        var qtySel = card.querySelector('[data-spb-qty-wrap]');
        if (qtyEl)  qtyEl.textContent = '0';
        if (addBtn) addBtn.hidden = false;
        if (qtySel) qtySel.hidden = true;
      }
      delete state.refills[variantId];
    }
    render();
  }

  // ── Helpers ──────────────────────────────────────────────────────
  function getRefillsSubtotal() {
    return Object.values(state.refills).reduce(function (s, r) { return s + r.price * r.qty; }, 0);
  }

  function isReady() {
    return state.bottleVariantId !== null;
  }

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function ctaStatusLabel() {
    if (!state.bottleVariantId) return 'Add a bottle to continue';
    return null; // ready
  }

  // ── Render ───────────────────────────────────────────────────────
  function render() {
    renderSummary();
    renderOrderCard();
    renderMobileFooter();
    if (dom.section) dom.section.classList.toggle('is-ready', isReady());
  }

  // ── Summary list (inline inside the order card) ────────────────────
  function renderSummary() {
    if (!dom.summaryList) return;

    if (!state.bottleVariantId) {
      if (dom.summaryInline) dom.summaryInline.hidden = true;
      dom.summaryList.innerHTML = '';
      return;
    }

    if (dom.summaryInline) dom.summaryInline.hidden = false;

    var hasRefills = Object.keys(state.refills).length > 0;

    var html = '';
    html += lineItemRow('bottle', state.bottleVariantId, state.bottleTitle, '', state.bottleSubtext, state.bottleImage, state.bottlePrice, 1);

    if (!hasRefills) {
      html += '<a href="#spb-step-refills" class="spb-summary-refill-prompt" data-scroll-refills>Add refills and save 15% today</a>';
    }

    Object.entries(state.refills).forEach(function (entry) {
      var id = entry[0], r = entry[1];
      if (r.qty > 0) html += lineItemRow('refill', id, r.title, r.tagline, r.subtext, r.image, r.price, r.qty);
    });

    dom.summaryList.innerHTML = html;

    dom.summaryList.querySelectorAll('.spb-line-remove').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var row = btn.closest('.spb-line-item');
        if (row) removeItem(row.dataset.removeType, row.dataset.removeId);
      });
    });
  }

  // ── Single order card (price + CTA) ───────────────────────────────
  function renderOrderCard() {
    var subtotalRaw  = state.bottlePrice + getRefillsSubtotal();
    var discounted   = Math.round(subtotalRaw * DISCOUNT);
    var ready        = isReady();
    var statusLabel  = ctaStatusLabel();

    var priceEl = document.querySelector('[data-fb-plan-price="order"]');
    if (priceEl) {
      priceEl.innerHTML = subtotalRaw > 0
        ? '<span class="fb-plan-row-price--sub">' + formatMoney(discounted) + '</span>' +
          ' <span class="fb-plan-row-price-compare">' + formatMoney(subtotalRaw) + '</span>'
        : '—';
    }

    document.querySelectorAll('[data-fb-cta="order"]').forEach(function (cta) {
      var isMobile = cta.classList.contains('fb-mobile-cta');
      cta.disabled = !ready;

      if (statusLabel) {
        cta.textContent = statusLabel;
      } else if (isMobile) {
        cta.innerHTML = subtotalRaw > 0
          ? 'Pre-order Now  ' + formatMoney(discounted) +
            ' <span class="fb-mobile-cta__compare">' + formatMoney(subtotalRaw) + '</span>'
          : 'Pre-order Now';
      } else {
        cta.textContent = 'Pre-Order Now';
      }
    });
  }

  // ── Mobile sticky footer — grows from 1 line to 2 once a bottle is picked.
  //    Price now lives on the CTA button (see renderOrderCard) — these rows
  //    are just labels: bottle name, then refill count. ──
  function renderMobileFooter() {
    if (!dom.mobileFooter) return;

    if (dom.mobileBottleLabel) {
      var mobileBottleTitle = state.bottleTitle.replace(/starter pack/i, '').replace(/\s+/g, ' ').trim();
      dom.mobileBottleLabel.textContent = state.bottleVariantId
        ? state.bottleSubtext + ' ' + mobileBottleTitle
        : 'Choose your bottle to continue';
    }

    if (dom.mobileBottleSwatch) {
      if (state.bottleVariantId) {
        dom.mobileBottleSwatch.style.background = state.bottleBg;
        dom.mobileBottleSwatch.hidden = false;
      } else {
        dom.mobileBottleSwatch.hidden = true;
      }
    }

    if (dom.mobileRefillsRow) dom.mobileRefillsRow.hidden = !state.bottleVariantId;

    if (dom.mobileRefillsText) {
      var refillQty = Object.values(state.refills).reduce(function (s, r) { return s + r.qty; }, 0);
      dom.mobileRefillsText.textContent = refillQty > 0
        ? '+' + refillQty + ' refill' + (refillQty !== 1 ? 's' : '')
        : 'Add refills and save 15% on entire order or';
    }
  }

  // ── Line item row ────────────────────────────────────────────────
  function lineItemRow(type, variantId, title, tagline, subtext, image, unitPrice, qty) {
    var linePrice  = unitPrice * qty;
    var discPrice  = Math.round(linePrice * DISCOUNT);
    var label      = type === 'bottle' ? title : qty + 'x ' + title;
    var safeLabel  = label.replace(/</g, '&lt;').replace(/>/g, '&gt;');
    var safeTitle  = title.replace(/"/g, '&quot;');
    var safeTag    = tagline ? tagline.replace(/</g, '&lt;').replace(/>/g, '&gt;') : '';
    var safeSub    = subtext ? subtext.replace(/</g, '&lt;').replace(/>/g, '&gt;') : '';

    var thumbHtml = image
      ? '<img class="spb-line-thumb" src="' + image + '" alt="' + safeTitle + '" loading="lazy">'
      : '<div class="spb-line-thumb"></div>';

    var taglineHtml = '';
    var displaySub  = safeSub;
    var subExtraClass = '';
    if (type === 'refill') {
      var totalKapsules = qty * SERVINGS_PER_REFILL;
      var perKapsule = Math.round(unitPrice * DISCOUNT) / 100 / SERVINGS_PER_REFILL;
      subExtraClass = ' spb-line-sub--green';
      displaySub = totalKapsules + ' Kapsules - $' + perKapsule.toFixed(2) + ' each';
    }
    var subtextHtml = displaySub ? '<p class="spb-line-sub text-meta' + subExtraClass + '">' + displaySub + '</p>' : '';

    var priceHtml = '<div class="spb-line-price">' +
        '<span class="spb-line-price-main">' + formatMoney(discPrice) + '</span>' +
        '<span class="spb-line-price-compare">' + formatMoney(linePrice) + '</span>' +
      '</div>';

    var removeHtml = type !== 'bottle'
      ? '<button type="button" class="spb-line-remove" aria-label="Remove ' + safeTitle + '">' +
          (dom.removeIconSvg || '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 4h10"/><path d="M6 4V2h4v2"/><path d="M5 4l1 9h4l1-9"/></svg>') +
        '</button>'
      : '';

    var bgAttr = (type === 'bottle' && state.bottleBg) ? ' style="background: ' + state.bottleBg + ';"' : '';

    return (
      '<div class="spb-line-item"' + bgAttr + ' data-remove-type="' + type + '" data-remove-id="' + variantId + '">' +
        thumbHtml +
        '<div class="spb-line-info">' +
          '<p class="spb-line-name">' + safeLabel + '</p>' +
          taglineHtml +
          subtextHtml +
        '</div>' +
        '<div class="spb-line-right">' + priceHtml + removeHtml + '</div>' +
      '</div>'
    );
  }

  // ── Add to cart → /checkout ───────────────────────────────────────
  function addToCart() {
    if (!isReady()) return;

    // Disable CTA while adding
    document.querySelectorAll('[data-fb-cta]').forEach(function (btn) {
      btn.disabled = true;
      btn.textContent = 'Adding…';
    });

    var existingBanner = document.getElementById('fb-atc-error');
    if (existingBanner) existingBanner.textContent = '';

    var items = [{
      id:         state.bottleVariantId,
      quantity:   1,
      properties: {},
    }];

    Object.entries(state.refills).forEach(function (entry) {
      var variantId = entry[0], r = entry[1];
      if (r.qty < 1) return;
      items.push({
        id:         parseInt(variantId, 10),
        quantity:   r.qty,
        properties: {},
      });
    });

    // Build flavor summary for cart attributes
    var flavorSummary = Object.values(state.refills)
      .filter(function (r) { return r.qty > 0; })
      .map(function (r) { return r.title + ' x' + r.qty; })
      .join(', ');

    var cartAttributes = {
      '_flavors': flavorSummary,
    };

    // Clear stale cart only if needed → add items → set attributes → redirect
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var doAdd = function () {
          return fetch('/cart/add.js', {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify({ items: items }),
          });
        };
        return cart.item_count > 0
          ? fetch('/cart/clear.js', { method: 'POST' }).then(doAdd)
          : doAdd();
      })
      .then(function (res) {
        if (!res.ok) throw new Error('Cart add failed: ' + res.status);
        return res.json();
      })
      .then(function () {
        return fetch('/cart/update.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ attributes: cartAttributes }),
        });
      })
      .then(function () {
        return fetch('/cart.js').then(function (r) { return r.json(); });
      })
      .then(function (cart) {
        syncCartCount(cart.item_count);
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cart } }));
        var url = '/discount/' + DISCOUNT_PCT + '?redirect=' + encodeURIComponent('/discount/' + DISCOUNT_SHIP + '?redirect=/checkout');
        window.location.href = url;
      })
      .catch(function (err) {
        console.error('[presale-builder-v4] ATC error:', err);

        var msg = 'Something went wrong — please try again.';
        if (err && err.message && err.message.indexOf('422') !== -1) {
          msg = 'One or more items are unavailable. Please refresh and try again.';
        }

        var banner = document.getElementById('fb-atc-error');
        if (!banner) {
          banner = document.createElement('p');
          banner.id = 'fb-atc-error';
          banner.style.cssText = 'color:#c0392b;font-size:0.95rem;margin:0.75rem 0 0;text-align:center;';
          var ctaWrap = document.querySelector('.fb-cta-wrap');
          if (ctaWrap) ctaWrap.appendChild(banner);
        }
        banner.textContent = msg;

        render(); // re-enables CTA
      });
  }

  // ── Cart count sync ───────────────────────────────────────────────
  // Dawn only renders .cart-count-bubble when cart != empty, so if the page
  // loaded with an empty cart the element doesn't exist in the DOM.
  // We create it when count transitions 0 → N, and remove it at N → 0.
  function syncCartCount(count) {
    var cartLink = document.querySelector('.header__icon--cart');
    if (!cartLink) return;

    var bubble = cartLink.querySelector('.cart-count-bubble');

    if (count > 0) {
      if (!bubble) {
        bubble = document.createElement('div');
        bubble.className = 'cart-count-bubble';
        bubble.innerHTML =
          '<span aria-hidden="true"></span>' +
          '<span class="visually-hidden">' + count + ' item' + (count === 1 ? '' : 's') + '</span>';
        cartLink.appendChild(bubble);
      }
      var countEl = bubble.querySelector('[aria-hidden="true"]');
      if (countEl) countEl.textContent = count;
    } else {
      if (bubble) bubble.remove();
    }
  }

  // ── Boot ─────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Sync cart count on every page show — handles bfcache restores AND
  // fresh loads where the cart bubble wasn't in the server-rendered HTML
  // (Dawn only renders the bubble when cart != empty at render time).
  window.addEventListener('pageshow', function () {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) { syncCartCount(cart.item_count); });
  });

})();
